import Button from '@/features/shared/Button';
import { AccordionFilter, FacetFilters } from '@/features/shared/ElasticComponent';
import ElasticComponentContext from '@/features/shared/ElasticComponent/ElasticComponentContext';
import { CUSTOM_FACETS, Theme } from '@/utils/constants';
import { orderBy } from 'lodash';
import React, { memo, PropsWithChildren, useContext } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import useStyles from './Sidebar.styles';

interface CompProps {
  theme?: ThemeType;
  labels?: { [key: string]: string };
  linksTitle?: string;
  links?: LinkType[];
  renderLabel?: any;
  isSorted?: boolean;
  onApplyFiltersDataLayer?(): void;
}

const Sidebar = ({
  links = [],
  linksTitle = 'Discover',
  labels,
  renderLabel,
  isSorted = false,
  onApplyFiltersDataLayer = () => {},
}: PropsWithChildren<CompProps>) => {
  const [allFilters, setAllFilters] = React.useState<any[]>([]);
  const { facets, callbackExternalFilters = () => null } = useContext(ElasticComponentContext);
  const classes = useStyles({});

  const buildFilterMenu = React.useCallback(() => {
    return facets
      .filter((filter) => filter.name)
      .map((filter: any) => ({
        ...filter,
        options: isSorted
          ? orderBy(filter.options, [({ value }) => value?.toLowerCase()], ['asc'])
          : filter.options,
      }));
  }, [facets, isSorted]);

  useDeepCompareEffect(() => {
    const sortedFilters = facets.length ? buildFilterMenu() : [];

    setAllFilters(sortedFilters);
  }, [facets, isSorted]);

  const customFacets = React.useMemo(
    () => facets?.filter((item) => CUSTOM_FACETS.has(item.name)),
    [facets],
  )?.sort((a, b) => a?.name?.localeCompare(b?.name));

  return (
    <div className={classes.root}>
      {!!customFacets.length && (
        <AccordionFilter
          title={labels?.developed_by || 'Developed by'}
          key="certification_level"
          theme={Theme.light}
          expanded
        >
          <div className={classes.filters}>
            {customFacets.map((item) => (
              <FacetFilters
                facet={item.name}
                key={item.name}
                options={item.options.filter((_item) => /true|byGenetec/.test(_item.value))}
                renderLabel={renderLabel}
                labels={labels}
                onChange={onApplyFiltersDataLayer}
              />
            ))}
          </div>
        </AccordionFilter>
      )}
      {!!links.length && (
        <AccordionFilter title={linksTitle} theme={Theme.light} expanded>
          <div className={classes.links}>
            {links.map((item, idx) => (
              <Button
                // eslint-disable-next-line react/no-array-index-key
                key={`link_${idx}`}
                variant="link"
                link={item}
                onClick={callbackExternalFilters}
                className={classes.link}
              />
            ))}
          </div>
        </AccordionFilter>
      )}
      {allFilters
        .filter((item) => !CUSTOM_FACETS.has(item.name))
        .map((item) => (
          <AccordionFilter title={item.label} key={item.name} theme={Theme.light} expanded>
            <div className={classes.filters}>
              <FacetFilters
                facet={item.name}
                options={item.options}
                labels={labels}
                hasScroll={false}
                onChange={onApplyFiltersDataLayer}
              />
            </div>
          </AccordionFilter>
        ))}
    </div>
  );
};

export default memo(Sidebar);
